import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Search, Sparkles, FileText, Users, CircleDot } from "lucide-react";
import { useLocaleFormat } from "@/lib/i18n-format";
import { useFilterByOwnerCompany } from "@/owner/OwnerCompanyContext";
import { CompanyBadge } from "@/owner/CompanyBadge";

export const Route = createFileRoute("/_app/estimates/")({
  component: EstimatesPage,
});

type EstimateRow = {
  id: string;
  number: string;
  title: string;
  status: "draft" | "sent" | "approved" | "rejected" | "expired";
  issue_date: string;
  expiry_date: string | null;
  total: number;
  currency: string;
  client_id: string;
  company_id: string;
  clients: { name: string } | null;
};

const statusVariant: Record<
  EstimateRow["status"],
  "default" | "secondary" | "destructive" | "outline"
> = {
  draft: "secondary",
  sent: "outline",
  approved: "default",
  rejected: "destructive",
  expired: "secondary",
};

function EstimatesPage() {
  const { t } = useTranslation();
  const { formatMoney, formatDate } = useLocaleFormat();
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState<EstimateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const statusOptions: EstimateRow["status"][] = [
    "draft",
    "sent",
    "approved",
    "rejected",
    "expired",
  ];

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("estimates")
        .select(
          "id, number, title, status, issue_date, expiry_date, total, currency, client_id, company_id, clients(name)",
        )
        .order("created_at", { ascending: false });
      if (error) {
        toast.error(t("pages.common.loadFailed"), { description: error.message });
      } else {
        setEstimates((data ?? []) as unknown as EstimateRow[]);
      }
      setLoading(false);
    })();
  }, [t]);

  const scoped = useFilterByOwnerCompany(estimates);

  const clientOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of scoped) {
      if (e.client_id && e.clients?.name) map.set(e.client_id, e.clients.name);
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [scoped]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = scoped;
    if (clientFilter !== "all") {
      rows = rows.filter((e) => e.client_id === clientFilter);
    }
    if (statusFilter !== "all") {
      rows = rows.filter((e) => e.status === statusFilter);
    }
    if (!q) return rows;
    return rows.filter((e) =>
      [e.number, e.title, e.clients?.name]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(q)),
    );
  }, [scoped, search, clientFilter, statusFilter]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("pages.estimates.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("pages.estimates.subtitle")}</p>
        </div>
        <Button onClick={() => navigate({ to: "/estimates/new" })} className="gap-2">
          <Plus className="h-4 w-4" />
          {t("pages.estimates.new")}
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <div className="flex flex-col gap-2 border-b border-border p-3 sm:flex-row sm:items-center">
          <div className="flex flex-1 items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("pages.common.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 border-0 bg-transparent shadow-none focus-visible:ring-0"
            />
          </div>
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="h-8 w-full sm:w-[200px]">
              <div className="flex items-center gap-2">
                <Users className="h-3.5 w-3.5 text-muted-foreground" />
                <SelectValue placeholder={t("pages.estimates.headers.client")} />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("pages.common.all", "Todos")}</SelectItem>
              {clientOptions.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-full sm:w-[160px]">
              <div className="flex items-center gap-2">
                <CircleDot className="h-3.5 w-3.5 text-muted-foreground" />
                <SelectValue placeholder={t("pages.estimates.headers.status")} />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("pages.common.all", "Todos")}</SelectItem>
              {statusOptions.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`statuses.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Badge variant="secondary" className="sm:ml-2">
            {t("pages.estimates.count", { count: filtered.length })}
          </Badge>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 p-12 text-center">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-full text-primary-foreground"
              style={{ background: "var(--gradient-brand)" }}
            >
              <Sparkles className="h-6 w-6" />
            </div>
            <div>
              <p className="font-medium">
                {estimates.length === 0 ? t("pages.estimates.empty") : t("pages.estimates.noMatch")}
              </p>
              <p className="text-sm text-muted-foreground">
                {estimates.length === 0
                  ? t("pages.estimates.emptyDesc")
                  : t("pages.common.tryDifferentSearch")}
              </p>
            </div>
            {estimates.length === 0 && (
              <Button onClick={() => navigate({ to: "/estimates/new" })} className="gap-2">
                <Plus className="h-4 w-4" />
                {t("pages.estimates.new")}
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("pages.estimates.headers.number")}</TableHead>
                  <TableHead>{t("pages.estimates.headers.client")}</TableHead>
                  <TableHead>{t("pages.estimates.headers.title")}</TableHead>
                  <TableHead>{t("pages.estimates.headers.status")}</TableHead>

                  <TableHead className="text-right">{t("pages.estimates.headers.total")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((e) => (
                  <TableRow key={e.id} className="cursor-pointer">
                    <TableCell className="font-mono text-xs">
                      <Link
                        to="/estimates/$estimateId"
                        params={{ estimateId: e.id }}
                        className="hover:underline"
                      >
                        {e.number}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <span>{e.clients?.name ?? "—"}</span>
                        <CompanyBadge companyId={e.company_id} />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Link
                        to="/estimates/$estimateId"
                        params={{ estimateId: e.id }}
                        className="flex items-center gap-2 font-medium hover:underline"
                      >
                        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                        {e.title}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariant[e.status]}>{t(`statuses.${e.status}`)}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatMoney(e.total, e.currency)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
